import { BaseAutoTracker } from './BaseAutoTracker';
import { GomarkePixelConfig } from '@/types';
import {
  CustomerDataStore,
  collectCustomerFieldInputs,
  detectCustomerFields,
  readFieldValue,
  type CustomerFieldKey,
  type CustomerFieldSelectors,
  type DetectedCustomerFields,
} from '@/conversion/customer';

const FIELD_EVENTS = ['input', 'change', 'blur'] as const;

export interface IAutoCustomerTrackerConfig {
  isEnabled: boolean;
  selectors?: CustomerFieldSelectors;
  debounceMs?: number;
}

/**
 * AutoTracker responsável por monitorar campos do cliente e manter o CustomerDataStore atualizado.
 * Independente do AutoAddPaymentInfo — apenas captura e persiste estado.
 */
export class AutoCustomerTracker extends BaseAutoTracker {
  private autoConfig?: IAutoCustomerTrackerConfig;
  private store = CustomerDataStore.getInstance();
  private fieldListenerEntries: Array<{
    element: HTMLElement;
    eventType: string;
    listener: (event: Event) => void;
  }> = [];
  private boundFields = new WeakSet<HTMLElement>();
  private detectedFields: DetectedCustomerFields | null = null;
  private fieldKeyByElement = new WeakMap<HTMLElement, CustomerFieldKey>();
  private syncTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = ''
  ) {
    super(config, sessionId, version, visitorId);
  }

  public async initialize(
    autoConfig: IAutoCustomerTrackerConfig
  ): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoCustomerTracker desabilitado');
      return;
    }

    this.autoConfig = autoConfig;
    this.log('Inicializando AutoCustomerTracker');

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupMonitoring());
    } else {
      await this.setupMonitoring();
    }

    this.observeDOM(() => this.setupMonitoring());
    this.isInitialized = true;
  }

  private async setupMonitoring(): Promise<void> {
    if (!this.autoConfig) return;

    this.detectedFields = detectCustomerFields(this.autoConfig.selectors);
    const inputs = collectCustomerFieldInputs(this.detectedFields);

    this.mapFieldKeys(this.detectedFields);
    this.syncStoreFromFields(this.detectedFields);

    inputs.forEach(input => {
      if (this.boundFields.has(input)) return;
      this.boundFields.add(input);

      FIELD_EVENTS.forEach(eventType => {
        const listener = () => this.scheduleStoreSync();
        input.addEventListener(eventType, listener);
        this.fieldListenerEntries.push({
          element: input,
          eventType,
          listener,
        });
      });
    });

    this.log(`Campos de cliente detectados: ${inputs.length}`);
  }

  private mapFieldKeys(fields: DetectedCustomerFields): void {
    for (const key of Object.keys(fields) as CustomerFieldKey[]) {
      const element = fields[key];
      if (element) {
        this.fieldKeyByElement.set(element, key);
      }
    }
  }

  private syncStoreFromFields(fields: DetectedCustomerFields): void {
    for (const key of Object.keys(fields) as CustomerFieldKey[]) {
      const value = readFieldValue(fields[key]);
      if (value) this.store.set(key, value);
    }
  }

  private scheduleStoreSync(): void {
    if (!this.autoConfig) return;

    const debounceMs = this.autoConfig.debounceMs ?? 300;

    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }

    this.syncTimeoutId = setTimeout(() => {
      if (!this.detectedFields) {
        this.detectedFields = detectCustomerFields(this.autoConfig?.selectors);
        this.mapFieldKeys(this.detectedFields);
      }

      for (const entry of this.fieldListenerEntries) {
        const key = this.fieldKeyByElement.get(entry.element);
        if (!key) continue;
        const value = readFieldValue(
          entry.element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        );
        if (value) this.store.set(key, value);
      }
    }, debounceMs);
  }

  private clearFieldListeners(): void {
    this.fieldListenerEntries.forEach(({ element, eventType, listener }) => {
      try {
        element.removeEventListener(eventType, listener);
      } catch {
        // elemento pode ter sido removido do DOM
      }
    });
    this.fieldListenerEntries = [];

    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
      this.syncTimeoutId = undefined;
    }
  }

  public stop(): void {
    this.clearFieldListeners();
    this.clearListeners();
    this.isInitialized = false;
    this.log('AutoCustomerTracker parado');
  }

  public async rebuild(): Promise<void> {
    if (this.autoConfig) {
      this.clearFieldListeners();
      this.boundFields = new WeakSet();
      await this.setupMonitoring();
    }
  }

  public isActive(): boolean {
    return this.isInitialized;
  }

  public getCustomerStats(): {
    detectedFieldCount: number;
    storeSnapshot: ReturnType<CustomerDataStore['get']>;
  } {
    const inputs = this.detectedFields
      ? collectCustomerFieldInputs(this.detectedFields)
      : [];

    return {
      detectedFieldCount: inputs.length,
      storeSnapshot: this.store.get(),
    };
  }

  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoCustomerTracker] ${message}`, data);
    }
  }
}
